import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight,
  BookOpen,
  Download,
  FileText,
  GitBranch,
  Lock,
  Mic,
  Shield,
  Sparkles,
  UserPlus,
  Users,
} from 'lucide-react';
import { BrandWordmark } from '../../components/brand/BrandWordmark';
import { CinematicHeroVisual } from './CinematicHeroVisual';
import './cinematic-landing.css';

/** Display-only copy aligned with Pricing.js (Stage 4B.1) — not billing logic */
const PRICING_PREVIEW = [
  {
    id: 'free',
    name: 'Start Your Legacy',
    price: 'Free',
    note: 'Preview your Legacy Book',
    highlights: ['Add loved ones', 'Save memories', 'Preview your book', 'Private by default'],
  },
  {
    id: 'legacy-book',
    name: 'Legacy Book',
    price: '£49',
    suffix: 'founding · one-time',
    badge: 'Finished keepsake',
    featured: true,
    highlights: ['Full Legacy Book PDF', 'More room to grow', 'QR link in keepsake', 'Re-export anytime'],
  },
  {
    id: 'family-archive',
    name: 'Family Archive',
    price: '£7.99/mo',
    suffix: 'coming soon',
    badge: 'Ongoing preservation',
    highlights: ['Voice & transcription', 'AI story polish', 'Family sharing', 'More storage'],
  },
];

const ARCHIVE_PLUS = {
  name: 'Family Archive',
  price: '£7.99/mo',
  alt: '£79/year',
  highlights: ['Subscription coming soon', 'Ongoing voice & AI tools', 'Join waitlist on Pricing'],
};

const PILLARS = [
  {
    icon: BookOpen,
    title: 'Preserve stories',
    body: 'Write memories in a calm story studio — prompts, chapters, and polished narratives that stay with your tree.',
  },
  {
    icon: Mic,
    title: 'Capture voices',
    body: 'Record voice notes beside written words. Tone, pauses, and laughter preserved as audio alongside transcription.',
  },
  {
    icon: Download,
    title: 'Export keepsakes',
    body: 'Turn your archive into PDFs and planned book exports — tangible heirlooms for family who prefer paper.',
  },
];

const HOW_IT_WORKS = [
  { step: '01', title: 'Add a person', body: 'Start with someone you love — a parent, grandparent, or chosen family member.', icon: UserPlus },
  { step: '02', title: 'Capture a memory', body: 'Write a story or record a voice note in Story Studio. Take your time.', icon: Mic },
  { step: '03', title: 'Attach photos, voice or documents', body: 'Cover photos, letters, certificates, and video links gather in one private place.', icon: FileText },
  { step: '04', title: 'Export a keepsake', body: 'Share a PDF or plan a legacy book export when your archive is ready.', icon: Download },
];

const fadeUp = (reduceMotion, delay = 0) =>
  reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 20 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-40px' },
        transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
      };

function SectionLabel({ children }) {
  return (
    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[hsl(43_40%_50%)]">{children}</p>
  );
}

function ProductPreviewPanel({ title, children, className = '' }) {
  return (
    <div className={`cine-glass flex flex-col overflow-hidden rounded-2xl ${className}`}>
      <div className="border-b border-[hsl(43_28%_48%/_0.12)] px-4 py-2.5">
        <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-[hsl(38_16%_55%)]">{title}</p>
      </div>
      <div className="flex-1 p-4">{children}</div>
    </div>
  );
}

export default function CinematicLandingExperiment() {
  const reduceMotion = useReducedMotion();
  const howRef = useRef(null);

  const scrollToHow = () => {
    howRef.current?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  };

  return (
    <div className="cine-landing cine-grain" data-testid="cinematic-landing-experiment">
      {/* Experimental banner */}
      <div className="relative z-10 border-b border-[hsl(43_28%_48%/_0.15)] bg-[hsl(158_12%_9%/_0.9)] px-4 py-2 text-center text-[11px] text-[hsl(38_16%_62%)]">
        Experimental concept — not the live homepage.{' '}
        <Link to="/home" className="text-[hsl(43_48%_58%)] underline-offset-2 hover:underline">
          View current site
        </Link>
      </div>

      {/* Nav */}
      <header className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-4 py-5 sm:px-6 lg:px-8">
        <Link to="/experimental/cinematic" className="transition opacity-90 hover:opacity-100">
          <BrandWordmark variant="navLanding" className="text-[hsl(42_28%_88%)]" />
        </Link>
        <nav className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/pricing"
            className="hidden text-sm text-[hsl(38_16%_62%)] transition hover:text-[hsl(42_28%_88%)] sm:inline"
          >
            Pricing
          </Link>
          <Link
            to="/login"
            className="rounded-full px-3 py-1.5 text-sm text-[hsl(38_16%_62%)] transition hover:text-[hsl(42_28%_88%)]"
          >
            Sign in
          </Link>
          <Link
            to="/register"
            className="cine-btn-primary rounded-full px-4 py-2 text-sm font-medium"
          >
            Start free
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="cine-hero-spotlight absolute inset-0" aria-hidden />
        <div className="relative z-10 mx-auto grid max-w-6xl gap-12 px-4 pb-20 pt-8 sm:px-6 lg:grid-cols-2 lg:items-center lg:gap-16 lg:px-8 lg:pb-28 lg:pt-12">
          <motion.div {...fadeUp(reduceMotion)}>
            <SectionLabel>Private family archive</SectionLabel>
            <h1 className="mt-4 font-serif text-3xl font-light leading-[1.15] tracking-tight sm:text-4xl lg:text-[2.65rem]">
              <span className="cine-text-gradient">
                Your family&apos;s stories, voices and memories — preserved in one private archive.
              </span>
            </h1>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-[hsl(38_16%_62%)] sm:text-lg">
              Build a living family archive with stories, voice memories, documents, timelines and keepsake exports.
              Private by default, designed for generations.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to="/register"
                className="cine-btn-primary inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
                data-testid="cinematic-cta-primary"
              >
                Start your family archive
                <ArrowRight className="h-4 w-4" />
              </Link>
              <button
                type="button"
                onClick={scrollToHow}
                className="cine-btn-secondary inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-medium"
                data-testid="cinematic-cta-secondary"
              >
                See how it works
              </button>
            </div>
            <p className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[hsl(38_16%_50%)]">
              <span className="inline-flex items-center gap-1.5">
                <Lock className="h-3.5 w-3.5 text-[hsl(43_40%_50%)]" aria-hidden />
                Private by default
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5 text-[hsl(43_40%_50%)]" aria-hidden />
                Your archive, your control
              </span>
            </p>
          </motion.div>

          <motion.div {...fadeUp(reduceMotion, 0.12)} className="relative lg:pl-4">
            <CinematicHeroVisual />
          </motion.div>
        </div>
      </section>

      {/* Pillars */}
      <section className="relative z-10 border-t border-[hsl(43_28%_48%/_0.1)] bg-[hsl(158_12%_8%/_0.5)] py-20 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div className="mx-auto max-w-2xl text-center" {...fadeUp(reduceMotion)}>
            <SectionLabel>Why families choose MyLegacyBridge</SectionLabel>
            <h2 className="mt-3 font-serif text-2xl font-light text-[hsl(42_28%_88%)] sm:text-3xl">
              An archive built for memory, not metrics
            </h2>
          </motion.div>
          <div className="mt-14 grid gap-6 sm:grid-cols-3 sm:gap-8">
            {PILLARS.map((p, i) => (
              <motion.article
                key={p.title}
                className="cine-glass rounded-2xl p-6 sm:p-7"
                {...fadeUp(reduceMotion, i * 0.08)}
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(152_28%_18%)] text-[hsl(43_48%_58%)]">
                  <p.icon className="h-5 w-5" aria-hidden />
                </span>
                <h3 className="mt-4 font-serif text-lg text-[hsl(42_28%_88%)]">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[hsl(38_16%_58%)]">{p.body}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Product preview */}
      <section className="relative z-10 py-20 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div className="max-w-xl" {...fadeUp(reduceMotion)}>
            <SectionLabel>Inside the archive</SectionLabel>
            <h2 className="mt-3 font-serif text-2xl font-light text-[hsl(42_28%_88%)] sm:text-3xl">
              One calm workspace for every kind of memory
            </h2>
          </motion.div>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            <ProductPreviewPanel title="Story studio" className="lg:col-span-2 lg:row-span-2">
              <p className="font-serif text-lg italic leading-relaxed text-[hsl(42_28%_88%/_0.9)]">
                &ldquo;Grandad always said the orchard smelled like rain&hellip;&rdquo;
              </p>
              <div className="mt-4 h-24 rounded-lg border border-[hsl(43_28%_48%/_0.12)] bg-[hsl(158_10%_10%/_0.6)] p-3">
                <p className="text-xs leading-relaxed text-[hsl(38_16%_55%)]">
                  Write at your pace. Voice notes sit beside the text. Deepen and polish when you are ready — never
                  rushed.
                </p>
              </div>
              <div className="mt-3 flex gap-2">
                <span className="rounded-full border border-[hsl(43_28%_48%/_0.2)] px-2.5 py-0.5 text-[10px] text-[hsl(38_16%_55%)]">
                  Preserve
                </span>
                <span className="rounded-full border border-[hsl(43_28%_48%/_0.2)] px-2.5 py-0.5 text-[10px] text-[hsl(38_16%_55%)]">
                  Record voice
                </span>
              </div>
            </ProductPreviewPanel>
            <ProductPreviewPanel title="Voice memory">
              <div className="flex h-full flex-col justify-center">
                <div className="flex items-end justify-center gap-1 py-2">
                  {WAVE_PREVIEW.map((h, i) => (
                    <span
                      key={i}
                      className="cine-wave-bar w-1 rounded-full bg-[hsl(43_48%_58%/_0.65)]"
                      style={{ height: `${h}px` }}
                    />
                  ))}
                </div>
                <p className="mt-3 text-center text-[11px] text-[hsl(38_16%_55%)]">3:42 · Transcribed</p>
              </div>
            </ProductPreviewPanel>
            <ProductPreviewPanel title="Document vault">
              <ul className="space-y-2 text-sm text-[hsl(38_16%_58%)]">
                <li className="flex items-center gap-2">
                  <FileText className="h-4 w-4 shrink-0 text-[hsl(43_48%_58%)]" />
                  Marriage certificate
                </li>
                <li className="flex items-center gap-2">
                  <FileText className="h-4 w-4 shrink-0 text-[hsl(43_48%_58%)]" />
                  War letter · 1944
                </li>
              </ul>
            </ProductPreviewPanel>
            <ProductPreviewPanel title="Family tree" className="sm:col-span-2 lg:col-span-2">
              <div className="flex items-center justify-center py-2">
                <GitBranch className="mr-2 h-5 w-5 text-[hsl(43_48%_58%)]" />
                <Users className="h-5 w-5 text-[hsl(38_16%_55%)]" />
              </div>
              <p className="text-center text-xs leading-relaxed text-[hsl(38_16%_55%)]">
                Genealogical connections, timelines, and person archives — each profile holds stories, documents, and
                voice memories together.
              </p>
            </ProductPreviewPanel>
          </div>
        </div>
      </section>

      {/* Privacy */}
      <section className="relative z-10 border-y border-[hsl(43_28%_48%/_0.1)] bg-[hsl(152_22%_10%/_0.35)] py-20 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center lg:gap-16">
            <motion.div {...fadeUp(reduceMotion)}>
              <SectionLabel>Privacy first</SectionLabel>
              <h2 className="mt-3 font-serif text-2xl font-light text-[hsl(42_28%_88%)] sm:text-3xl">
                A family vault, not a social feed
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-[hsl(38_16%_58%)] sm:text-base">
                MyLegacyBridge is private by default. You choose who is invited into your archive. Stories, voice
                recordings, and documents stay within your family space — designed for trust across generations, not
                public performance.
              </p>
            </motion.div>
            <motion.ul className="space-y-4" {...fadeUp(reduceMotion, 0.1)}>
              {[
                { icon: Lock, text: 'Private placeholders until you choose to share a profile' },
                { icon: Users, text: 'Invite family editors — not open discovery' },
                { icon: Shield, text: 'Export your data when you want a keepsake or backup' },
                { icon: Sparkles, text: 'No generic AI chatbot — tools serve your archive, not the other way around' },
              ].map((item) => (
                <li key={item.text} className="cine-glass flex gap-3 rounded-xl px-4 py-3">
                  <item.icon className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(43_48%_58%)]" aria-hidden />
                  <span className="text-sm text-[hsl(38_16%_62%)]">{item.text}</span>
                </li>
              ))}
            </motion.ul>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section ref={howRef} id="how-it-works" className="relative z-10 py-20 sm:py-24 scroll-mt-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div className="mx-auto max-w-2xl text-center" {...fadeUp(reduceMotion)}>
            <SectionLabel>How it works</SectionLabel>
            <h2 className="mt-3 font-serif text-2xl font-light text-[hsl(42_28%_88%)] sm:text-3xl">
              From first name to family heirloom
            </h2>
          </motion.div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {HOW_IT_WORKS.map((step, i) => (
              <motion.div
                key={step.step}
                className="relative rounded-2xl border border-[hsl(43_28%_48%/_0.12)] bg-[hsl(158_10%_10%/_0.4)] p-5"
                {...fadeUp(reduceMotion, i * 0.06)}
              >
                <span className="text-[10px] font-mono text-[hsl(43_40%_45%)]">{step.step}</span>
                <step.icon className="mt-3 h-5 w-5 text-[hsl(43_48%_58%)]" aria-hidden />
                <h3 className="mt-3 font-serif text-base text-[hsl(42_28%_88%)]">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[hsl(38_16%_55%)]">{step.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="relative z-10 border-t border-[hsl(43_28%_48%/_0.1)] bg-[hsl(158_12%_8%/_0.5)] py-20 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div className="mx-auto max-w-2xl text-center" {...fadeUp(reduceMotion)}>
            <SectionLabel>Pricing preview</SectionLabel>
            <h2 className="mt-3 font-serif text-2xl font-light text-[hsl(42_28%_88%)] sm:text-3xl">
              Pay once for your archive. Add tools only if you need them.
            </h2>
            <p className="mt-3 text-sm text-[hsl(38_16%_55%)]">
              Full details on the{' '}
              <Link to="/pricing" className="text-[hsl(43_48%_58%)] underline-offset-2 hover:underline">
                pricing page
              </Link>
              . No checkout changes here.
            </p>
          </motion.div>
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {PRICING_PREVIEW.map((plan, i) => (
              <motion.article
                key={plan.id}
                className={`cine-glass relative flex flex-col rounded-2xl p-6 ${
                  plan.featured ? 'ring-1 ring-[hsl(43_48%_58%/_0.35)]' : ''
                }`}
                {...fadeUp(reduceMotion, i * 0.07)}
              >
                {plan.badge ? (
                  <span className="mb-3 inline-block self-start rounded-full border border-[hsl(43_40%_50%/_0.3)] px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[hsl(43_48%_58%)]">
                    {plan.badge}
                  </span>
                ) : null}
                <h3 className="font-serif text-xl text-[hsl(42_28%_88%)]">{plan.name}</h3>
                <p className="mt-2">
                  <span className="text-2xl font-light text-[hsl(42_28%_88%)]">{plan.price}</span>
                  {plan.suffix ? (
                    <span className="ml-1 text-xs text-[hsl(38_16%_50%)]">{plan.suffix}</span>
                  ) : null}
                </p>
                <p className="mt-1 text-xs text-[hsl(38_16%_50%)]">{plan.note}</p>
                <ul className="mt-5 flex-1 space-y-2 text-sm text-[hsl(38_16%_58%)]">
                  {plan.highlights.map((h) => (
                    <li key={h} className="flex gap-2">
                      <span className="text-[hsl(43_48%_58%)]">·</span>
                      {h}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  className={`mt-6 block rounded-full py-2.5 text-center text-sm font-medium ${
                    plan.featured ? 'cine-btn-primary' : 'cine-btn-secondary'
                  }`}
                >
                  {plan.id === 'free' ? 'Start free' : `Explore ${plan.name}`}
                </Link>
              </motion.article>
            ))}
          </div>

          <motion.div
            className="cine-glass mx-auto mt-8 max-w-3xl rounded-2xl p-6 sm:p-8"
            {...fadeUp(reduceMotion, 0.15)}
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-[hsl(43_40%_50%)]">Add-on</p>
                <h3 className="mt-1 font-serif text-xl text-[hsl(42_28%_88%)]">{ARCHIVE_PLUS.name}</h3>
                <p className="mt-1 text-sm text-[hsl(38_16%_55%)]">
                  {ARCHIVE_PLUS.price} · or {ARCHIVE_PLUS.alt}
                </p>
              </div>
              <Link
                to="/pricing"
                className="cine-btn-secondary shrink-0 rounded-full px-5 py-2 text-sm font-medium sm:mt-1"
              >
                View Archive Plus
              </Link>
            </div>
            <ul className="mt-5 grid gap-2 sm:grid-cols-3 text-xs text-[hsl(38_16%_55%)]">
              {ARCHIVE_PLUS.highlights.map((h) => (
                <li key={h}>{h}</li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 overflow-hidden py-24 sm:py-32">
        <div className="cine-glow-radial absolute inset-0" aria-hidden />
        <motion.div
          className="relative mx-auto max-w-3xl px-4 text-center sm:px-6"
          {...fadeUp(reduceMotion)}
        >
          <SectionLabel>Begin quietly</SectionLabel>
          <h2 className="mt-4 font-serif text-3xl font-light leading-snug text-[hsl(42_28%_88%)] sm:text-4xl">
            The stories only your family can tell deserve a place that lasts.
          </h2>
          <p className="mx-auto mt-5 max-w-lg text-sm leading-relaxed text-[hsl(38_16%_58%)] sm:text-base">
            Start with one person. Add a memory when you are ready. Your archive grows at the pace of real life.
          </p>
          <Link
            to="/register"
            className="cine-btn-primary mt-10 inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-sm font-semibold"
          >
            Start your family archive
            <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-[hsl(43_28%_48%/_0.1)] px-4 py-10 text-center text-xs text-[hsl(38_16%_45%)]">
        <p>MyLegacyBridge · Experimental cinematic concept</p>
        <p className="mt-2">
          <Link to="/home" className="hover:text-[hsl(38_16%_62%)]">
            Production homepage
          </Link>
          {' · '}
          <Link to="/pricing" className="hover:text-[hsl(38_16%_62%)]">
            Pricing
          </Link>
          {' · '}
          <Link to="/faq" className="hover:text-[hsl(38_16%_62%)]">
            FAQ
          </Link>
        </p>
      </footer>
    </div>
  );
}

const WAVE_PREVIEW = [10, 18, 14, 22, 12, 20, 16];
